// The Home screen's POP tag scan, as a hook (Session APG). "POP" is Proof Of Presence: the NFC sticker
// on the paper log card in a lift's motor room.
//
// WHY IT EXISTS, in the Director's words on 21 September 2026: the tag is programmed per LIFT, so
// reading it already says where the engineer is, and making them open a particular job before they can
// tap it is the wrong shape. One scan button on Home, always there, finds the job itself.
//
// THE MECHANISM IS NOT REBUILT. Reading the tag (the live sign-in preflight, the Web NFC reader, the
// thirty second timeout, the wrong-sticker messages, the abort on leaving the screen) is
// usePopTagScan.ts, the same hook the job sheet's two tag steps and the standalone scan screen use, and
// so is posting the tap and interpreting the answer. This file adds only what a scan that does not yet
// know its job needs, in this order:
//
//   1. the hook reads a tag and hands over the token (its `onRead` option);
//   2. ASK THE SERVER which of this engineer's open service visits and call-outs at that lift (today, overdue or undated) the scan could belong to
//      (POST /api/engineer/pop-tag/match: read only), and which end each is waiting for;
//   3. NONE: record the tap with no job, as a tap has always been recorded, and say plainly that it is
//      linked to nothing. ONE: record it for that job and that end, with no question. SEVERAL: ask the
//      engineer which job, once per tap, and record it for the one they choose;
//   4. tell the job sheet (noteTap) so the sheet shows that end as recorded.
//
// THE ONE EXCEPTION TO "NO QUESTION FOR ONE JOB", added after the adversarial review. The end a scan is
// comes from the job's own taps, so a second press soon after an arrival reads as the LEAVING. For a real
// visit that is right; for the two ordinary accidents the platform's three-minute repeat window exists
// to absorb (a press whose answer was lost with the signal after the server had already written the row,
// and a second press with the phone still on the tag) it would log a leaving seconds after the arrival,
// a fabricated visit of no length on the tap record, the sheet's pre-filled times and the timesheet. So
// when the job's arrival is inside that window the phone asks "Leaving already?" first. The rule is
// unchanged; an engineer who really is leaving has one extra tap, and nobody else is ever asked.
//
// THE TAP IS WRITTEN BY THE SAME ROUTE AS THE JOB SHEET'S OWN STEP (recordNow -> POST
// /api/public/tap/:token with { direction, jobId }), which is what makes the two surfaces one record.
// Nothing here writes a tap of its own and nothing here knows how a tap is judged (an install
// confirmation, a repeat inside three minutes): the server decides, and the answer is read back.
//
// THE PICKER DECIDES WHICH JOB AND NEVER WHICH END. Each job's own recorded taps say which end it is
// waiting for, the server reports that beside every candidate, and this file sends exactly what it was
// told. Nothing on the phone computes an end.
//
// WITHOUT SIGNAL there is nothing to match against: a tag's token identifies its lift only to the
// server (the token never reaches the phone as anything but the opaque string just read). The scan
// then says so and records nothing, and the job sheet's own tag step, which already knows its job,
// still works and still falls back to the phone's clock.
//
// NOTHING THAT HAS BEEN READ IS EVER DROPPED IN SILENCE. Every way out of a scan after the tag has been
// read (cancelling while the server is asked, cancelling the question, declining "Leaving already?", a
// failed lookup) ends on a message that says nothing was recorded, and clears the sign-in warning that
// described a scan which is no longer going ahead.
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { ApiError, api, type PopTagCandidate, type PopTagMatch } from "../../lib/api";
import {
  homeScanSummary,
  leaveQuestion,
  needsLeaveConfirm,
  type HomeScanSummary,
  type LeaveQuestion,
  type PopTagEnd,
} from "../../lib/popTagHome";
import { useEngineerData } from "./EngineerDataContext";
import { londonTime, usePopTagScan } from "./usePopTagScan";

// Where the scan has got to BETWEEN the hook's own phases. The hook owns scanning, posting and the
// answer; this covers the things it does not know about.
type Stage =
  | { kind: "idle" }
  // The token has been read and the server is being asked which of the engineer's jobs it belongs to.
  | { kind: "matching" }
  // More than one job is open at this lift: waiting for the engineer to say which this tap is for.
  | { kind: "choosing"; token: string; match: PopTagMatch }
  // The one job (or the one chosen) had its arrival recorded moments ago: waiting for the engineer to say
  // they really are leaving, before a leaving is logged.
  | { kind: "confirming"; token: string; match: PopTagMatch; job: PopTagCandidate }
  // The lookup failed, so nothing was recorded. The message says what to do.
  | { kind: "failed"; message: string }
  // The engineer backed out of the scan after the tag was read. Nothing was recorded, and the panel says so.
  | { kind: "cancelled" };

export interface HomeTagScan {
  // True when this browser can read a tag at all. The button is shown either way (Home always has
  // it); a phone that cannot read one is told so, in place, when it is pressed.
  supported: boolean;
  // Whether pressing the button would do anything right now.
  busy: boolean;
  // What is happening this moment, in the engineer's words; null when nothing is.
  status: string | null;
  // Set while the engineer is being asked which job this tap is for.
  choosing: PopTagMatch | null;
  // Set while the engineer is being asked whether they really are leaving, a moment after arriving.
  confirming: LeaveQuestion | null;
  // What just happened, once it has; null until then.
  summary: HomeScanSummary | null;
  // A failure worth saying, from the read, the lookup or the recording; null otherwise.
  message: string | null;
  // The sign-in could not be confirmed and the scan went ahead anyway (see usePopTagScan). Only ever true
  // while that scan is still going ahead: it never outlives the scan it describes.
  unconfirmed: boolean;
  // A phone that cannot read a tag was asked to.
  unsupportedShown: boolean;
  // The engineer backed out of the scan after the tag was read, and nothing was recorded.
  cancelled: boolean;
  // A scan can be stopped while it is checking, waiting for a tag, asking the server or asking the engineer.
  cancellable: boolean;
  press: () => void;
  cancel: () => void;
  choose: (candidate: PopTagCandidate) => void;
  // "Yes, log my leaving": go ahead with the leaving the engineer was just asked about.
  confirm: () => void;
  dismiss: () => void;
}

export function useHomeTagScan(): HomeTagScan {
  const { noteTap } = useEngineerData();
  const [stage, setStage] = useState<Stage>({ kind: "idle" });
  const [unsupportedShown, setUnsupportedShown] = useState(false);
  // What the tap being recorded is FOR, kept so the answer can name the job and the site.
  const [recording, setRecording] = useState<{ match: PopTagMatch; job: PopTagCandidate | null } | null>(null);

  // Bumped whenever a lookup is abandoned (Cancel, leaving the screen), so an answer that arrives
  // late is ignored instead of recording a tap nobody is waiting on.
  const runRef = useRef(0);
  useEffect(
    () => () => {
      runRef.current += 1;
    },
    [],
  );

  // The read handler is created before the hook it feeds, so it is reached through a ref that is
  // refreshed after every commit.
  const handleReadRef = useRef<(token: string) => void>(() => undefined);
  const scan = usePopTagScan({
    actionLabel: "press the scan button",
    onRead: (token) => handleReadRef.current(token),
  });
  const { recordNow } = scan;

  // Record the tap for the job chosen (or for none) and remember what it was for.
  const recordFor = useCallback(
    async (token: string, match: PopTagMatch, job: PopTagCandidate | null) => {
      setStage({ kind: "idle" });
      setRecording({ match, job });
      // A body only when there is a job to name. With none, the tap is posted exactly as the
      // standalone screen posts it (no body at all): recorded against the lift, linked to nothing.
      await recordNow(token, job ? { direction: job.nextEnd, jobId: job.jobId } : undefined);
    },
    [recordNow],
  );

  // Record the tap, unless it is a leaving inside the repeat window of the job's own arrival, in which
  // case ask first. The one place that decision is made, so the single-job path and the picker cannot differ.
  const proceed = useCallback(
    async (token: string, match: PopTagMatch, job: PopTagCandidate | null) => {
      if (job && needsLeaveConfirm(job)) {
        setStage({ kind: "confirming", token, match, job });
        return;
      }
      await recordFor(token, match, job);
    },
    [recordFor],
  );

  // What happens when the hook hands over a token: ask the server, then record, ask, or say why not.
  const handleRead = useCallback(
    (token: string) => {
      const run = ++runRef.current;
      setStage({ kind: "matching" });
      void (async () => {
        let match: PopTagMatch;
        try {
          match = await api.engineer.popTagMatch(token);
          // A reply that is not the shape asked for is a FAILED LOOKUP, never a crash. apiRequest hands
          // back `undefined` for a success that is not JSON, and a captive portal on guest or site wifi
          // answers a POST with a 200 page: without this the next line threw, outside any catch, and left
          // the panel on "Finding your job..." for ever with the tag thrown away in silence.
          if (!match || !Array.isArray(match.candidates)) throw new Error("unexpected reply to the job lookup");
        } catch (err) {
          if (run !== runRef.current) return;
          // "That tag is not in use" is for the route's OWN answer (its bare {error: "not_found"} for a
          // malformed, unknown or cleared tag) and for nothing else. A 404 from a server that predates
          // this route, or from anything in between, is not about the tag, and telling the engineer their
          // sticker is broken would send them to the office and lose a tap the old screen would have kept.
          const notInUse = err instanceof ApiError && err.status === 404 && err.code === "not_found";
          setStage({
            kind: "failed",
            message: notInUse
              ? "That tag is not in use. Ask the office to check it."
              : "Could not check your jobs just now, so nothing was recorded. Check your signal and press the scan button again, or open the job and tap the tag from its sheet.",
          });
          return;
        }
        if (run !== runRef.current) return;
        if (match.candidates.length > 1) {
          setStage({ kind: "choosing", token, match });
          return;
        }
        await proceed(token, match, match.candidates[0] ?? null);
      })();
    },
    [proceed],
  );
  useEffect(() => {
    handleReadRef.current = handleRead;
  });

  // Tell the job sheet, once per landed tap. Only a tap the platform really logged for the job: an
  // install confirmation, a tap that could not be linked (no name reached the server) and a repeat that
  // joined somebody else's row all say nothing about THIS job's ends.
  const notedRef = useRef<string | null>(null);
  useEffect(() => {
    if (scan.phase !== "done" || !scan.result || !recording?.job) return;
    if (scan.outcome !== "recorded_named" && scan.outcome !== "collapsed_mine") return;
    const end: PopTagEnd =
      scan.result.direction === "arrival" || scan.result.direction === "departure" ? scan.result.direction : recording.job.nextEnd;
    const key = `${recording.job.jobId}|${end}|${scan.result.tappedAt}`;
    if (notedRef.current === key) return;
    notedRef.current = key;
    void noteTap(recording.job.jobId, end, scan.result.tappedAt);
  }, [scan.phase, scan.result, scan.outcome, recording, noteTap]);

  const summary = useMemo<HomeScanSummary | null>(() => {
    if (scan.phase !== "done" || !scan.result || !scan.outcome || !recording) return null;
    return homeScanSummary({
      assetRef: scan.result.assetRef,
      siteName: recording.match.siteName,
      job: recording.job,
      result: scan.result,
      outcome: scan.outcome,
      time: londonTime(scan.result.tappedAt),
      noJobReason: recording.match.reason ?? null,
      otherType: recording.match.otherType ?? null,
    });
  }, [scan.phase, scan.result, scan.outcome, recording]);

  const confirming = useMemo<LeaveQuestion | null>(() => {
    if (stage.kind !== "confirming") return null;
    return leaveQuestion({
      siteName: stage.match.siteName,
      assetRef: stage.match.assetRef,
      job: stage.job,
      arrivedTime: stage.job.arrivedAt ? londonTime(stage.job.arrivedAt) : null,
    });
  }, [stage]);

  const hookBusy = scan.phase === "checking" || scan.phase === "scanning" || scan.phase === "posting";
  const stageBusy = stage.kind === "matching" || stage.kind === "choosing" || stage.kind === "confirming";

  const status = useMemo(() => {
    if (scan.phase === "checking") return "Checking you are signed in...";
    if (scan.phase === "scanning") return "Hold the back of your phone against the sticker. Keep the screen on.";
    if (stage.kind === "matching") return "Finding your job...";
    if (scan.phase === "posting") return "Recording...";
    return null;
  }, [scan.phase, stage.kind]);

  const press = useCallback(() => {
    if (hookBusy || stageBusy) return;
    if (!scan.supported) {
      setUnsupportedShown(true);
      return;
    }
    // A new scan starts from a clean panel: the last answer, question and message are put away.
    setUnsupportedShown(false);
    setStage({ kind: "idle" });
    setRecording(null);
    void scan.scan();
  }, [hookBusy, stageBusy, scan]);

  const cancel = useCallback(() => {
    if (stage.kind === "matching" || stage.kind === "choosing" || stage.kind === "confirming") {
      runRef.current += 1;
      // The tag has ALREADY been read, so this is a tap being set aside, and it says so (the panel's
      // "Nothing was recorded"). Nothing else needs undoing: the reader stopped when the tag was read, and
      // the sign-in warning is only ever shown while a scan is going ahead (see `unconfirmed` below), so it
      // goes as soon as the stage leaves matching. (An earlier draft also called scan.cancel() here; a
      // mutation run showed it changed nothing anyone could see, so it was removed rather than kept as a
      // second mechanism for the same job.)
      setStage({ kind: "cancelled" });
      return;
    }
    scan.cancel();
  }, [stage.kind, scan]);

  const choose = useCallback(
    (candidate: PopTagCandidate) => {
      if (stage.kind !== "choosing") return;
      void proceed(stage.token, stage.match, candidate);
    },
    [stage, proceed],
  );

  const confirm = useCallback(() => {
    if (stage.kind !== "confirming") return;
    void recordFor(stage.token, stage.match, stage.job);
  }, [stage, recordFor]);

  const dismiss = useCallback(() => {
    runRef.current += 1;
    scan.reset();
    setStage({ kind: "idle" });
    setRecording(null);
    setUnsupportedShown(false);
  }, [scan]);

  return {
    supported: scan.supported,
    busy: hookBusy || stageBusy,
    status,
    choosing: stage.kind === "choosing" ? stage.match : null,
    confirming,
    summary,
    message: stage.kind === "failed" ? stage.message : scan.message,
    // The warning describes a scan that is going ahead, so it lasts exactly as long as one is: while the
    // hook is checking, waiting or posting, or the server is being asked. Once the scan has ended any way
    // (cancelled, failed, answered) it is gone, whatever the shared hook still holds.
    unconfirmed: scan.unconfirmed && (hookBusy || stage.kind === "matching"),
    unsupportedShown,
    cancelled: stage.kind === "cancelled",
    cancellable: scan.phase === "checking" || scan.phase === "scanning" || stageBusy,
    press,
    cancel,
    choose,
    confirm,
    dismiss,
  };
}
